import React from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import InfoIcon from '@mui/icons-material/Info';
import useSEO from '../hooks/useSEO';

const TermsPage: React.FC = () => {
    useSEO({
        title: 'قوانین و شرایط چیستا',
        description: 'قوانین و شرایط استفاده از دایرکتوری کسب‌وکارهای ایرانیان چیستا.',
        canonical: '/cheesta-terms',
    });

    const terms = [
        'چیستا مسئولیتی در قبال کسب و کارهای ثبت شده در سایت ندارد و صحت اطلاعات وارد شده به عهده شخص ثبت کننده میباشد',
        'موارد و مسائل خلاف اخلاق در سایت ثبت و تایید نمیگردد',
        'وجود جملات نادرست از نظر املائی یا نگارشی تصحیح یا حذف خواهند شد',
        'مواردی که شائبه کلاهبرداری در آن مشاهده شود حذف خواهند شد',
        'آگهی نباید موجب توهین به ادیان رسمی کشور ، آداب ، رسوم ، قومیتها ، لهجه ها و گویشهای مختلف شود .',
        'کسب و کارهای بدون عکس و یا اطلاعات غلط در سایت تایید نمیگردد',
        'استفاده ابزاری از تصاویر اشخاص ، درج بی‌مورد عکس صورت اشخاص یا استفاده از عکس کودکان برای معرفی کالا و خدماتی که مخاطب آن کودکان نیستند، ثبت نخواهند شد.',
        'هر گونه کالایی که موجب رواج خشونت و آسیب رساندن به دیگران می‌شود، مانند: سلاح‌های گرم و سرد، مواد محترقه و امثال آن تایید نخواهد شد.',
        'حق نشر تصاویر: تصاویر آپلود شده باید متعلق به صاحب کسب و کار باشد و رعایت حقوق کپی‌رایت بر عهده کاربر است.',
        'لینک‌های وب‌سایت: چیستا مسئولیتی در قبال محتوای وب‌سایت‌های خارجی که در پروفایل کسب‌ و کارها درج شده است، ندارد.',
        'بروزرسانی محتوا: صاحبان کسب و کار موظفند در صورت تغییر آدرس، تلفن یا خدمات، اطلاعات خود را در سایت بروزرسانی کنند.',
        'نظرات و نقدها: نظرات ثبت شده توسط کاربران باید صرفاً بر اساس تجربه واقعی و بدون توهین باشد؛ نظرات نامناسب تایید نخواهند شد.',
        'تغییر قوانین: چیستا این حق را برای خود محفوظ می‌دارد که در هر زمان قوانین و شرایط استفاده از سایت را بروزرسانی کند.',
    ];

    return (
        <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: { xs: 6, md: 10 } }}>
            {/* Hero Section */}
            <Box
                sx={{
                    width: '100%',
                    height: { xs: 150, md: 225 },
                    position: 'relative',
                    overflow: 'hidden',
                    mb: -4
                }}
            >
                <Box
                    component="img"
                    src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1200&q=80"
                    alt="Cheesta Terms and Conditions"
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.6))',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 900,
                            color: 'white',
                            fontSize: { xs: '2rem', md: '3rem' },
                            textShadow: '0 4px 15px rgba(0,0,0,0.6)',
                            textAlign: 'center',
                            px: 2
                        }}
                    >
                        قوانین و شرایط چیستا
                    </Typography>
                </Box>
            </Box>

            <Container maxWidth="md">
                <Paper elevation={4} sx={{ p: { xs: 3, md: 6 }, borderRadius: 6, position: 'relative', zIndex: 2 }}>
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#718096',
                                fontWeight: 600,
                                direction: 'rtl',
                                fontSize: '1.1rem'
                            }}
                        >
                            لطفا موارد زیر به هنگام ثبت آگهی بررسی شود:
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 6 }} />

                    {/* Terms List */}
                    <List sx={{ direction: 'rtl' }}>
                        {terms.map((term, index) => (
                            <ListItem key={index} sx={{ mb: 2, alignItems: 'flex-start' }}>
                                <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                                    <GavelIcon sx={{ color: '#2854a1' }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary={term}
                                    primaryTypographyProps={{
                                        fontSize: '1rem',
                                        lineHeight: 1.8,
                                        color: '#2d3748',
                                    }}
                                    sx={{ textAlign: 'right' }}
                                />
                            </ListItem>
                        ))}
                    </List>

                    <Box sx={{ mt: 8, p: 4, bgcolor: 'rgba(40, 84, 161, 0.05)', borderRadius: 4, borderRight: '4px solid #2854a1' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, direction: 'rtl' }}>
                            <InfoIcon sx={{ color: '#2854a1', ml: 1 }} />
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#2854a1' }}>
                                درباره چیستا
                            </Typography>
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{
                                lineHeight: 1.8,
                                color: '#4a5568',
                                textAlign: 'justify',
                                direction: 'rtl',
                                fontSize: '0.95rem'
                            }}
                        >
                            چیستا مرجع کسب و کارهای ایرانیان مقیم استرالیا تلاش میکند تا جامعه نه چندان کوچک ایرانیان کسب و کارهای خود را به صورت رایگان معرفی نمایند و هموطنان عزیز همدیگر را سریع تر و راحت تر بیایند و این جامعه با موفقیت در استرالیا رشد و پیشرفت نماید
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default TermsPage;
